import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Flex, useBreakpointValue } from "@chakra-ui/react";

import { PanViewer } from "react-image-pan-zoom-rotate";

import { AiOutlineZoomIn, AiOutlineZoomOut } from "react-icons/ai";
import PDFViewer from "./pdfViewer";

type ReactPanZoomProps = {
  image: any;
  alt?: string;
  ref?: any;
  type?: string;
};

type DragPosition = { x: number; y: number };

export const ReactPanZoom = ({ image, alt, type, ref }: ReactPanZoomProps) => {
  const [dx, setDx] = React.useState(0);
  const [dy, setDy] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [flip, setFlip] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const MIN_ZOOM = 0.3;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.2;

  const resetAll = () => {
    setDx(0);
    setDy(0);
    setZoom(1);
    setRotation(0);
    setFlip(false);
  };

  const zoomIn = () => {
    setZoom((prevZoom) => Math.min(MAX_ZOOM, prevZoom + ZOOM_STEP));
  };

  const zoomOut = () => {
    setZoom((prevZoom) => Math.max(MIN_ZOOM, prevZoom - ZOOM_STEP));
  };

  const rotateLeft = () => {
    if (rotation === -3) {
      setRotation(0);
    } else {
      setRotation(rotation - 1);
    }
  };

  const flipImage = () => {
    setFlip(!flip);
  };

  const onPan = (dx: number, dy: number) => {
    setDx(dx);
    setDy(dy);
  };

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.deltaY < 0) {
      setZoom((prevZoom) => Math.min(MAX_ZOOM, prevZoom + ZOOM_STEP));
    } else {
      setZoom((prevZoom) => Math.max(MIN_ZOOM, prevZoom - ZOOM_STEP));
    }
  };

  // Touch pinch zoom support
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      (e.currentTarget as HTMLElement).dataset.initialDistance = distance.toString();
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      const initialDistance = parseFloat(
        (e.currentTarget as HTMLElement).dataset.initialDistance || "0"
      );
      if (initialDistance > 0) {
        const scale = distance / initialDistance;
        setZoom((prevZoom) => {
          const newZoom = prevZoom * scale;
          return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
        });
        (e.currentTarget as HTMLElement).dataset.initialDistance = distance.toString();
      }
    }
  };

  // Attach wheel event listener
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      <div
        style={{
          position: "absolute",
          right: "10px",
          zIndex: 2,
          top: 10,
          userSelect: "none",
          borderRadius: 2,
          background: "#fff",
          boxShadow: "0px 2px 6px rgba(53, 67, 93, 0.32)",
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
            zoomIn();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            textAlign: "center",
            cursor: "pointer",
            height: 40,
            width: 40,
            borderBottom: " 1px solid #ccc",
          }}
        >
          <svg
            style={{
              height: "100%",
              width: "100%",
              padding: 10,
              boxSizing: "border-box",
            }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 12H20"
              stroke="#4C68C1"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M12 4L12 20"
              stroke="#4C68C1"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            zoomOut();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            textAlign: "center",
            cursor: "pointer",
            height: 40,
            width: 40,
            borderBottom: " 1px solid #ccc",
          }}
        >
          <svg
            style={{
              height: "100%",
              width: "100%",
              padding: 10,
              boxSizing: "border-box",
            }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 12H20"
              stroke="#4C68C1"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div
          onClick={(e) => {
            e.stopPropagation();
            rotateLeft();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            textAlign: "center",
            cursor: "pointer",
            height: 40,
            width: 40,
            borderBottom: " 1px solid #ccc",
          }}
        >
          <svg
            style={{
              height: "100%",
              width: "100%",
              padding: 10,
              boxSizing: "border-box",
            }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 15L9 20L4 15"
              stroke="#4C68C1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 4H13C10.7909 4 9 5.79086 9 8V20"
              stroke="#4C68C1"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        {/* <div
                    onClick={flipImage}
                    style={{
                        textAlign: 'center',
                        cursor: 'pointer',
                        height: 40,
                        width: 40,
                        borderBottom: ' 1px solid #ccc',
                    }}
                >
                    <svg
                        style={{
                            height: '100%',
                            width: '100%',
                            padding: 10,
                            boxSizing: 'border-box',
                        }}
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            stroke="#4C68C1"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.178,18.799V1.763L0,18.799H9.178z M8.517,18.136h-7.41l7.41-13.752V18.136z"
                        />
                        <polygon
                            stroke="#4C68C1"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            points="11.385,1.763 11.385,18.799 20.562,18.799 "
                        />
                    </svg>
                </div> */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            resetAll();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            textAlign: "center",
            cursor: "pointer",
            height: 40,
            width: 40,
          }}
        >
          <svg
            style={{
              height: "100%",
              width: "100%",
              padding: 10,
              boxSizing: "border-box",
            }}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#4C68C1"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
      <PanViewer
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1,
          touchAction: "none",
        }}
        zoom={zoom}
        setZoom={setZoom}
        pandx={dx}
        pandy={dy}
        onPan={onPan}
        rotation={rotation}
      >
        <img
          style={{
            transform: `rotate(${rotation * 90}deg) scaleX(${flip ? -1 : 1})`,
            width: "100%",
          }}
          src={image}
          alt={alt}
          ref={ref}
        />
      </PanViewer>
    </div>
  );
};

type Sample = {
  file: File | null;
  type: string | null;
  url: string;
  buffer: Uint8Array | null;
};

export default function FileViewer({ sample }: { sample: Sample }) {
  const [fileType, setFileType] = useState<string>("pdf");
  const viewerWidth = useBreakpointValue({ lg: true });

  useEffect(() => {
    if (sample.url) {
      if (sample?.type === "pdf") {
        setFileType("pdf");
      } else if (sample?.type?.includes("image")) {
        setFileType("image");
      }
    }
  }, [sample]);

  return (
    <>
      {fileType === "pdf" ? (
        <>
          {viewerWidth ? (
            <embed
              width="100%"
              height="100%"
              className="customScrollBar"
              src={sample?.url}
              type="application/pdf"
            ></embed>
          ) : (
            <PDFViewer pdfUrl={sample?.url} />
          )}
        </>
      ) : (
        <Box
          position="relative"
          h="100%"
          w="100%"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ReactPanZoom alt="uploaded image template" image={sample?.url} />
        </Box>
      )}
    </>
  );
}
